/**
 * Route Guards
 *
 * Provides components for protecting routes based on:
 * - Authentication status
 * - User roles
 * - Permissions
 * - Subscription features
 */

import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import {
  useAuth,
  usePermissions,
  useRole,
  useCanDo,
  useFeatureAccess,
} from '../../context/AuthContext';
import type { UserRole, Permission, PlanFeatures } from '../../lib/rbac';

// ============================================
// AUTHENTICATION GUARDS
// ============================================

interface AuthGuardProps {
  children: ReactNode;
  /** Where to redirect if not authenticated */
  redirectTo?: string;
  /** Allow anonymous sessions */
  allowAnonymous?: boolean;
}

/**
 * Requires user to be authenticated (or anonymous if allowed)
 */
export function AuthGuard({
  children,
  redirectTo = '/onboarding',
  allowAnonymous = true,
}: AuthGuardProps) {
  const { isAuthenticated, isAnonymous, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <LoadingFallback />;
  }

  const hasAccess = isAuthenticated || (allowAnonymous && isAnonymous);

  if (!hasAccess) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

/**
 * Requires user to be fully authenticated (not anonymous)
 */
export function RequireAuth({ children, redirectTo = '/onboarding' }: AuthGuardProps) {
  return (
    <AuthGuard allowAnonymous={false} redirectTo={redirectTo}>
      {children}
    </AuthGuard>
  );
}

// ============================================
// ROLE-BASED GUARDS
// ============================================

interface RoleGuardProps {
  children: ReactNode;
  /** Single role or array of roles (any match grants access) */
  roles: UserRole | UserRole[];
  /** Component to show if access denied */
  fallback?: ReactNode;
  /** Where to redirect if access denied (overrides fallback) */
  redirectTo?: string;
}

/**
 * Requires user to have one of the specified roles
 */
export function RoleGuard({ children, roles, fallback, redirectTo }: RoleGuardProps) {
  const { isLoading } = useAuth();
  const permissions = usePermissions();

  if (isLoading) {
    return <LoadingFallback />;
  }

  const roleArray = Array.isArray(roles) ? roles : [roles];
  const hasRole = roleArray.some(role => permissions.isRole(role));

  if (!hasRole) {
    if (redirectTo) {
      return <Navigate to={redirectTo} replace />;
    }
    return <>{fallback ?? <AccessDenied reason="You don't have the required role to view this page." />}</>;
  }

  return <>{children}</>;
}

/**
 * Guard for admin-only pages
 */
export function AdminGuard({ children, fallback, redirectTo }: Omit<RoleGuardProps, 'roles'>) {
  return (
    <RoleGuard roles={['org_admin', 'super_admin']} fallback={fallback} redirectTo={redirectTo}>
      {children}
    </RoleGuard>
  );
}

/**
 * Guard for counselor and admin pages
 */
export function CounselorGuard({ children, fallback, redirectTo }: Omit<RoleGuardProps, 'roles'>) {
  return (
    <RoleGuard roles={['counselor', 'org_admin', 'super_admin']} fallback={fallback} redirectTo={redirectTo}>
      {children}
    </RoleGuard>
  );
}

/**
 * Guard for parent portal
 */
export function ParentGuard({ children, fallback, redirectTo }: Omit<RoleGuardProps, 'roles'>) {
  return (
    <RoleGuard roles={['parent', 'org_admin', 'super_admin']} fallback={fallback} redirectTo={redirectTo}>
      {children}
    </RoleGuard>
  );
}

/**
 * Guard for super admin (platform-level) pages
 */
export function SuperAdminGuard({ children, fallback, redirectTo }: Omit<RoleGuardProps, 'roles'>) {
  return (
    <RoleGuard roles="super_admin" fallback={fallback} redirectTo={redirectTo}>
      {children}
    </RoleGuard>
  );
}

// ============================================
// PERMISSION-BASED GUARDS
// ============================================

interface PermissionGuardProps {
  children: ReactNode;
  /** Single permission or array of permissions */
  permissions: Permission | Permission[];
  /** Require all permissions (default: any) */
  requireAll?: boolean;
  /** Component to show if access denied */
  fallback?: ReactNode;
  /** Where to redirect if access denied */
  redirectTo?: string;
}

/**
 * Requires user to have specific permissions
 */
export function PermissionGuard({
  children,
  permissions: requiredPermissions,
  requireAll = false,
  fallback,
  redirectTo,
}: PermissionGuardProps) {
  const { isLoading } = useAuth();
  const perms = usePermissions();

  if (isLoading) {
    return <LoadingFallback />;
  }

  const permArray = Array.isArray(requiredPermissions) ? requiredPermissions : [requiredPermissions];
  const hasAccess = requireAll
    ? perms.hasAllPermissions(permArray)
    : perms.hasAnyPermission(permArray);

  if (!hasAccess) {
    if (redirectTo) {
      return <Navigate to={redirectTo} replace />;
    }
    return <>{fallback ?? <AccessDenied reason="You don't have permission to view this page." />}</>;
  }

  return <>{children}</>;
}

// ============================================
// FEATURE/SUBSCRIPTION GUARDS
// ============================================

interface FeatureGuardProps {
  children: ReactNode;
  /** Feature key from subscription plan */
  feature: keyof PlanFeatures;
  /** Component to show if feature not available */
  fallback?: ReactNode;
  /** Where to redirect if feature not available */
  redirectTo?: string;
}

/**
 * Requires user's subscription to include a specific feature
 */
export function FeatureGuard({ children, feature, fallback, redirectTo }: FeatureGuardProps) {
  const { isLoading } = useAuth();
  const hasFeature = useFeatureAccess(feature);

  if (isLoading) {
    return <LoadingFallback />;
  }

  if (!hasFeature) {
    if (redirectTo) {
      return <Navigate to={redirectTo} replace />;
    }
    return <>{fallback ?? <UpgradePrompt feature={feature} />}</>;
  }

  return <>{children}</>;
}

// ============================================
// COMBINED GUARDS
// ============================================

interface CombinedGuardProps {
  children: ReactNode;
  /** Require authentication */
  requireAuth?: boolean;
  /** Allow anonymous sessions */
  allowAnonymous?: boolean;
  /** Required roles (any match) */
  roles?: UserRole | UserRole[];
  /** Required permissions */
  permissions?: Permission | Permission[];
  /** Require all permissions */
  requireAllPermissions?: boolean;
  /** Required subscription feature */
  feature?: keyof PlanFeatures;
  /** Fallback for any denial */
  fallback?: ReactNode;
  /** Redirect for any denial */
  redirectTo?: string;
}

/**
 * Combined guard that checks multiple conditions
 */
export function Guard({
  children,
  requireAuth = false,
  allowAnonymous = true,
  roles,
  permissions,
  requireAllPermissions = false,
  feature,
  fallback,
  redirectTo,
}: CombinedGuardProps) {
  let content = <>{children}</>;

  // Apply guards from most specific to least specific
  if (feature) {
    content = (
      <FeatureGuard feature={feature} fallback={fallback} redirectTo={redirectTo}>
        {content}
      </FeatureGuard>
    );
  }

  if (permissions) {
    content = (
      <PermissionGuard
        permissions={permissions}
        requireAll={requireAllPermissions}
        fallback={fallback}
        redirectTo={redirectTo}
      >
        {content}
      </PermissionGuard>
    );
  }

  if (roles) {
    content = (
      <RoleGuard roles={roles} fallback={fallback} redirectTo={redirectTo}>
        {content}
      </RoleGuard>
    );
  }

  if (requireAuth) {
    content = (
      <AuthGuard allowAnonymous={allowAnonymous} redirectTo={redirectTo ?? '/onboarding'}>
        {content}
      </AuthGuard>
    );
  }

  return content;
}

// ============================================
// RENDER PROP GUARDS
// ============================================

interface CanProps {
  /** Permission to check */
  do: Permission;
  /** Render when allowed */
  children: ReactNode;
  /** Render when denied (optional) */
  else?: ReactNode;
}

/**
 * Conditionally render based on permission
 */
export function Can({ do: permission, children, else: fallback }: CanProps) {
  const allowed = useCanDo(permission);
  return <>{allowed ? children : fallback}</>;
}

interface HasRoleProps {
  /** Role to check */
  role: UserRole;
  /** Render when user has role */
  children: ReactNode;
  /** Render when user doesn't have role */
  else?: ReactNode;
}

/**
 * Conditionally render based on role
 */
export function HasRole({ role, children, else: fallback }: HasRoleProps) {
  const hasRole = useRole(role);
  return <>{hasRole ? children : fallback}</>;
}

interface HasFeatureProps {
  /** Feature to check */
  feature: keyof PlanFeatures;
  /** Render when feature available */
  children: ReactNode;
  /** Render when feature not available */
  else?: ReactNode;
}

/**
 * Conditionally render based on subscription feature
 */
export function HasFeature({ feature, children, else: fallback }: HasFeatureProps) {
  const hasFeature = useFeatureAccess(feature);
  return <>{hasFeature ? children : fallback}</>;
}

// ============================================
// FALLBACK COMPONENTS
// ============================================

function LoadingFallback() {
  return (
    <div className="flex items-center justify-center min-h-[200px]">
      <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
    </div>
  );
}

interface AccessDeniedProps {
  reason?: string;
}

function AccessDenied({ reason }: AccessDeniedProps) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[300px] text-center px-4">
      <div className="text-4xl mb-4">🔒</div>
      <h2 className="text-xl font-semibold text-text-primary mb-2">Access Denied</h2>
      <p className="text-text-secondary max-w-md">
        {reason ?? "You don't have permission to access this page."}
      </p>
    </div>
  );
}

interface UpgradePromptProps {
  feature: keyof PlanFeatures;
}

function UpgradePrompt({ feature }: UpgradePromptProps) {
  const featureNames: Record<keyof PlanFeatures, string> = {
    journal: 'Journal',
    basic_techniques: 'Basic Techniques',
    full_technique_library: 'Full Technique Library',
    ai_coach: 'Guided Reflection',
    brain_gym: 'Brain Gym',
    modules_limit: 'Additional Modules',
    advanced_analytics: 'Advanced Analytics',
    parent_dashboard: 'Parent Dashboard',
    family_achievements: 'Family Achievements',
    admin_dashboard: 'Admin Dashboard',
    cohort_analytics: 'Cohort Analytics',
    intervention_tools: 'Intervention Tools',
    data_export: 'Data Export',
    custom_branding: 'Custom Branding',
    sso: 'Single Sign-On',
    api_access: 'API Access',
    dedicated_support: 'Dedicated Support',
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-[300px] text-center px-4">
      <div className="text-4xl mb-4">✨</div>
      <h2 className="text-xl font-semibold text-text-primary mb-2">Upgrade Required</h2>
      <p className="text-text-secondary max-w-md mb-4">
        {featureNames[feature] ?? feature} is available on our premium plans.
      </p>
      <a
        href="/settings#subscription"
        className="px-4 py-2 bg-primary text-primary-text rounded-lg hover:bg-primary-hover transition-colors"
      >
        View Plans
      </a>
    </div>
  );
}

// ============================================
// EXPORTS
// ============================================

export {
  LoadingFallback,
  AccessDenied,
  UpgradePrompt,
};

